/**
 * Format claim/evidence text as readable prose.
 *
 * Some claim text arrives as a JSON-encoded object: the backend's
 * `_claim_text` helper falls back to `json.dumps(...)` for structured field
 * values, so the payload is a real string at the type level but raw
 * `{"key":"value",...}` structure once rendered (§defect #26b). Detect that
 * shape and turn it into a readable "Key: value" phrase instead of showing
 * the JSON verbatim.
 */

/** Split camelCase / snake_case tokens into a Title-cased phrase. */
function humanizeKey(key: string): string {
  const spaced = key
    .replace(/([a-z0-9])([A-Z])/g, "$1 $2")
    .replace(/[_-]+/g, " ")
    .trim()
  if (!spaced) return key
  return spaced.charAt(0).toUpperCase() + spaced.slice(1)
}

function proseFromParsedValue(value: unknown): string {
  if (value == null || value === "") return ""
  if (typeof value === "string" || typeof value === "number" || typeof value === "boolean") {
    return String(value)
  }
  if (Array.isArray(value)) {
    return value
      .map((item) => proseFromParsedValue(item))
      .filter(Boolean)
      .join("; ")
  }
  if (typeof value === "object") {
    return Object.entries(value as Record<string, unknown>)
      .map(([key, entryValue]) => {
        const rendered = proseFromParsedValue(entryValue)
        return rendered ? `${humanizeKey(key)}: ${rendered}` : ""
      })
      .filter(Boolean)
      .join("; ")
  }
  return String(value)
}

/**
 * If `value` is a JSON object/array-shaped string, parse it and return a
 * readable "Key: value; Key: value" phrase. Returns `null` for anything else
 * (plain prose, a non-string, or text that fails to parse) so the caller can
 * fall back to rendering it unchanged.
 */
export function formatStructuredClaimText(value: unknown): string | null {
  if (typeof value !== "string") return null

  const trimmed = value.trim()
  if (!trimmed || (trimmed[0] !== "{" && trimmed[0] !== "[")) return null

  let parsed: unknown
  try {
    parsed = JSON.parse(trimmed)
  } catch {
    return null
  }

  if (typeof parsed !== "object" || parsed === null) return null

  return proseFromParsedValue(parsed) || null
}
